import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Music } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Cours', path: '/cours' },
    { name: 'Tarifs', path: '/tarifs' },
    { name: 'À propos', path: '/a-propos' },
    { name: 'Avis', path: '/avis' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-md py-2' : 'bg-transparent py-4'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="navbar-logo"
          >
            <div className="w-10 h-10 bg-burgundy rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <Music className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="font-dancing text-2xl text-burgundy">École de Musique Pour Tous</span>
              <p className="text-xs text-gray-600 -mt-1 font-nunito">Périgueux</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-nunito font-semibold text-sm transition-colors relative group ${
                  location.pathname === link.path
                    ? 'text-burgundy'
                    : 'text-gray-700 hover:text-burgundy'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-coral transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+33649523778"
              className="flex items-center gap-2 px-4 py-2 border-2 border-burgundy text-burgundy rounded-full font-semibold text-sm hover:bg-burgundy hover:text-white transition-all"
              data-testid="navbar-phone-btn"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">06 49 52 37 78</span>
            </a>
            <Link
              to="/contact"
              className="px-6 py-2 bg-burgundy text-white rounded-full font-semibold text-sm hover:bg-burgundy-600 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              data-testid="navbar-cta-btn"
            >
              Essai gratuit
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-burgundy" />
            ) : (
              <Menu className="w-6 h-6 text-burgundy" />
            )}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-gray-200"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-xl font-nunito font-semibold transition-colors ${
                    location.pathname === link.path
                      ? 'bg-burgundy text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 space-y-3">
                <a
                  href="tel:+33649523778"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-burgundy text-burgundy rounded-xl font-semibold hover:bg-burgundy hover:text-white transition-all"
                  data-testid="mobile-phone-btn"
                >
                  <Phone className="w-5 h-5" />
                  06 49 52 37 78
                </a>
                <Link
                  to="/contact"
                  className="block w-full px-4 py-3 bg-burgundy text-white rounded-xl font-semibold text-center hover:bg-burgundy-600 transition-all"
                  data-testid="mobile-cta-btn"
                >
                  Réserver un essai gratuit
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
